import {
    Button,
    Chip,
    Divider,
    Flex,
    MultiSelect,
    Pill,
    Popover,
    Select,
    Text
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { FilterListOutlined } from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import { TIMES, useEntityListQuery } from "@therapprove/common";
import { FC, useEffect, useRef } from "react";
import {
    AGE_RANGES,
    DAY_ABBREVIATIONS,
    DISTANCE_OPTIONS
} from "./globalReferralFilters.consts";
import {
    defaultGlobalReferralFilters,
    type GlobalReferralFilterState
} from "./useGlobalReferralFilters";

// Section label style
const sectionLabelStyle = {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: "var(--mantine-color-gray-6)",
    textTransform: "uppercase" as const,
    padding: "0.25rem 0.5rem 0.1rem"
};

type GlobalReferralFiltersProps = {
    filters: GlobalReferralFilterState;
    setFilters: (filters: GlobalReferralFilterState) => void;
};

// Helpers
const hasFilters = (filters: GlobalReferralFilterState) =>
    filters.distanceMiles !== undefined ||
    filters.ageRange !== undefined ||
    filters.therapyCategoryIds.length > 0 ||
    filters.conditionIds.length > 0 ||
    filters.daysAvailable.length > 0 ||
    filters.timesAvailable.length > 0;

/**
 * Filter panel button — uses the same manual-Popover pattern as
 * WebsiteLeadFilters so that MultiSelect interactions don't close it.
 */
export const GlobalReferralFilterButton: FC<GlobalReferralFiltersProps> = ({
    filters,
    setFilters
}) => {
    // Queries
    const [{ data: entityData }] = useEntityListQuery();
    const conditions = entityData?.conditions?.conditionsList ?? [];
    const therapyCategories =
        entityData?.therapyCategories?.therapyCategoriesList ?? [];

    // Data
    const conditionOptions = conditions
        .filter(Boolean)
        .map((c) => ({ value: c!.value, label: c!.label }));
    const therapyCategoryOptions = therapyCategories
        .filter(Boolean)
        .map((t) => ({ value: t!.value, label: t!.label }));
    const hasActiveFilters = hasFilters(filters);

    // State
    const [opened, { toggle, close }] = useDisclosure(false);
    const popoverRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Handlers
    const clearFilters = () => setFilters(defaultGlobalReferralFilters);

    // Manual click-outside handler that ignores clicks on nested
    // portal dropdowns (MultiSelect combobox options).
    useEffect(() => {
        if (!opened) return;

        const handleClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (!target) return;
            if (buttonRef.current?.contains(target)) return;
            if (popoverRef.current?.contains(target)) return;

            const nestedPopoverDropdown = target.closest(
                ".mantine-Popover-dropdown"
            );
            if (
                nestedPopoverDropdown &&
                nestedPopoverDropdown !== popoverRef.current
            )
                return;

            close();
        };

        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [opened, close]);

    return (
        <Popover
            opened={opened}
            position="bottom-start"
            shadow="md"
            trapFocus={false}
            closeOnClickOutside={false}
        >
            <Popover.Target>
                <Button
                    ref={buttonRef}
                    variant="default"
                    leftSection={<SvgIcon component={FilterListOutlined} />}
                    onClick={toggle}
                >
                    Filters
                </Button>
            </Popover.Target>
            <Popover.Dropdown
                ref={popoverRef}
                style={{
                    width: "25rem",
                    maxHeight: "24rem",
                    overflowY: "auto",
                    padding: "0.5rem 0"
                }}
            >
                {/* Location Proximity */}
                <Text style={sectionLabelStyle}>Location Proximity</Text>
                <div style={{ padding: "0 0.5rem 0.25rem" }}>
                    <Select
                        placeholder="Select distance"
                        data={DISTANCE_OPTIONS}
                        value={
                            filters.distanceMiles
                                ? String(filters.distanceMiles)
                                : null
                        }
                        onChange={(value) =>
                            setFilters({
                                ...filters,
                                distanceMiles: value
                                    ? parseInt(value)
                                    : undefined
                            })
                        }
                        comboboxProps={{ withinPortal: true }}
                        clearable
                        size="xs"
                    />
                </div>

                {/* Age */}
                <Divider my={4} />
                <Text style={sectionLabelStyle}>Age</Text>
                <div style={{ padding: "0 0.5rem 0.25rem" }}>
                    <Select
                        placeholder="Select age range"
                        data={AGE_RANGES}
                        value={filters.ageRange ?? null}
                        onChange={(value) =>
                            setFilters({
                                ...filters,
                                ageRange: value ?? undefined
                            })
                        }
                        comboboxProps={{ withinPortal: true }}
                        clearable
                        size="xs"
                    />
                </div>

                {/* Therapy Modality */}
                <Divider my={4} />
                <Text style={sectionLabelStyle}>Therapy Modality</Text>
                <div style={{ padding: "0 0.5rem 0.25rem" }}>
                    <MultiSelect
                        variant="pill"
                        placeholder="Select modalities"
                        data={therapyCategoryOptions}
                        value={filters.therapyCategoryIds}
                        onChange={(value) =>
                            setFilters({
                                ...filters,
                                therapyCategoryIds: value
                            })
                        }
                        comboboxProps={{ withinPortal: true }}
                        searchable
                        clearable
                        size="xs"
                    />
                </div>

                {/* Conditions */}
                <Divider my={4} />
                <Text style={sectionLabelStyle}>Conditions</Text>
                <div style={{ padding: "0 0.5rem 0.25rem" }}>
                    <MultiSelect
                        variant="pill"
                        placeholder="Select conditions"
                        data={conditionOptions}
                        value={filters.conditionIds}
                        onChange={(value) =>
                            setFilters({
                                ...filters,
                                conditionIds: value
                            })
                        }
                        comboboxProps={{ withinPortal: true }}
                        searchable
                        clearable
                        size="xs"
                    />
                </div>

                {/* Days Available */}
                <Divider my={4} />
                <Text style={sectionLabelStyle}>Days Available</Text>
                <div style={{ padding: "0.25rem 0.5rem" }}>
                    <Chip.Group
                        multiple
                        value={filters.daysAvailable}
                        onChange={(value: string[]) =>
                            setFilters({
                                ...filters,
                                daysAvailable: value
                            })
                        }
                    >
                        <Flex gap={4} wrap="nowrap">
                            {DAY_ABBREVIATIONS.map((day) => (
                                <Chip
                                    key={day.value}
                                    value={day.value}
                                    size="xs"
                                    variant="compact"
                                    checked={filters.daysAvailable.includes(
                                        day.value
                                    )}
                                >
                                    {day.label}
                                </Chip>
                            ))}
                        </Flex>
                    </Chip.Group>
                </div>

                {/* Times Available */}
                <Divider my={4} />
                <Text style={sectionLabelStyle}>Times Available</Text>
                <div style={{ padding: "0.25rem 0.5rem" }}>
                    <Chip.Group
                        multiple
                        value={filters.timesAvailable}
                        onChange={(value: string[]) =>
                            setFilters({
                                ...filters,
                                timesAvailable: value
                            })
                        }
                    >
                        <Flex gap={4} wrap="nowrap">
                            {TIMES.map((time) => (
                                <Chip
                                    key={time}
                                    value={time}
                                    size="xs"
                                    variant="compact"
                                    checked={filters.timesAvailable.includes(
                                        time
                                    )}
                                >
                                    {time}
                                </Chip>
                            ))}
                        </Flex>
                    </Chip.Group>
                </div>

                {/* Clear all */}
                {hasActiveFilters && (
                    <>
                        <Divider my={4} />
                        <Button
                            variant="subtle"
                            size="compact-xs"
                            c="dimmed"
                            fullWidth
                            onClick={clearFilters}
                        >
                            Clear filters
                        </Button>
                    </>
                )}
            </Popover.Dropdown>
        </Popover>
    );
};

/**
 * The active filter pills row — rendered below the header.
 */
export const GlobalReferralFilterPills: FC<GlobalReferralFiltersProps> = ({
    filters,
    setFilters
}) => {
    // Queries
    const [{ data: entityData }] = useEntityListQuery();
    const conditions = entityData?.conditions?.conditionsList ?? [];
    const therapyCategories =
        entityData?.therapyCategories?.therapyCategoriesList ?? [];

    // Data
    const hasActiveFilters = hasFilters(filters);
    if (!hasActiveFilters) return null;

    // Helpers
    const getLabel = (key: string, value: string): string => {
        switch (key) {
            case "conditionIds":
                return (
                    conditions.find((c) => c?.value === value)?.label ?? value
                );
            case "therapyCategoryIds":
                return (
                    therapyCategories.find((t) => t?.value === value)?.label ??
                    value
                );
            case "daysAvailable":
                return (
                    DAY_ABBREVIATIONS.find((d) => d.value === value)?.label ??
                    value
                );
            default:
                return value;
        }
    };

    // Handlers
    const toggleArrayValue = (
        key: keyof GlobalReferralFilterState,
        value: string
    ) => {
        const current = filters[key] as string[];
        setFilters({ ...filters, [key]: current.filter((v) => v !== value) });
    };

    const removePill = (key: string, value: string) => {
        if (key === "ageRange") {
            setFilters({ ...filters, ageRange: undefined });
        } else if (key === "distanceMiles") {
            setFilters({ ...filters, distanceMiles: undefined });
        } else {
            toggleArrayValue(key as keyof GlobalReferralFilterState, value);
        }
    };

    // Build pills in display order
    const pills: { key: string; value: string; label: string }[] = [];

    if (filters.distanceMiles)
        pills.push({
            key: "distanceMiles",
            value: String(filters.distanceMiles),
            label: `${filters.distanceMiles} miles`
        });
    if (filters.ageRange)
        pills.push({
            key: "ageRange",
            value: filters.ageRange,
            label:
                AGE_RANGES.find((r) => r.value === filters.ageRange)?.label ??
                filters.ageRange
        });
    for (const id of filters.therapyCategoryIds)
        pills.push({
            key: "therapyCategoryIds",
            value: id,
            label: getLabel("therapyCategoryIds", id)
        });
    for (const id of filters.conditionIds)
        pills.push({
            key: "conditionIds",
            value: id,
            label: getLabel("conditionIds", id)
        });
    for (const day of filters.daysAvailable)
        pills.push({
            key: "daysAvailable",
            value: day,
            label: getLabel("daysAvailable", day)
        });
    for (const time of filters.timesAvailable)
        pills.push({ key: "timesAvailable", value: time, label: time });

    return (
        <Flex gap={6} align="center" wrap="wrap">
            <Pill
                size="xs"
                style={{
                    cursor: "pointer",
                    backgroundColor: "var(--mantine-color-secondary-5)",
                    color: "white"
                }}
                onClick={() => setFilters(defaultGlobalReferralFilters)}
            >
                Clear all
            </Pill>
            {pills.map((pill) => (
                <Pill
                    key={`${pill.key}-${pill.value}`}
                    size="xs"
                    withRemoveButton
                    onRemove={() => removePill(pill.key, pill.value)}
                >
                    {pill.label}
                </Pill>
            ))}
        </Flex>
    );
};
